// src/pages/BrandList.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Snackbar, Alert, TextField,
  Pagination, Stack, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { getAllBrands, deleteBrand } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormBrand from './FormBrand';
import SortableTh from './common/SortableTh';
import { useServerSort } from '../hooks/useServerSort';

function BrandList() {
  // paginação
  const [pagina, setPagina] = useState(1);
  const [qtde] = useState(20);

  // dados
  const [items, setItems] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  // busca (mesmo padrão da CategoryList)
  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');

  // ui
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState({ open: false, id: null });

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('descricao', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregar = useCallback(async () => {
    try {
      const { data } = await getAllBrands({ indice: pagina, qtde, termo, ordenar: orderBy, direcao: order });
      setItems(data.items ?? []);
      setTotalPaginas(data.totalPages ?? 1);
      setTotalItens(data.totalItems ?? 0);
    } catch {
      setSnackMsg('Erro ao carregar marcas');
      setSnackSeverity('error');
      setSnackOpen(true);
    }
  }, [pagina, qtde, termo, orderBy, order]);

  useEffect(() => { carregar(); }, [carregar]);

  const sorted = items;

  const onBuscar = () => { setPagina(1); setTermo(searchText.trim()); };
  const onKeyDown = (e) => { if (e.key === 'Enter') onBuscar(); };

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnackMsg(msg);
    setSnackSeverity('success');
    setSnackOpen(true);
    carregar();
  };

  const excluir = async () => {
    try {
      await deleteBrand(confirmId);
      setSnackMsg('Marca excluída');
      setSnackSeverity('success');
      setSnackOpen(true);
      setConfirmId(null);
      // se removemos o único item da página e há páginas anteriores, volta uma
      if (items.length === 1 && pagina > 1) setPagina(pagina - 1);
      else carregar();
    } catch (e) {
      setConfirmId(null);
      setSnackMsg(getApiErrorMessage(e, 'Não foi possível excluir a marca.'));
      setSnackSeverity('error');
      setSnackOpen(true);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Marcas"
        subtitle="Fabricantes e marcas usadas para agrupar e filtrar produtos."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
            Nova marca
          </Button>
        }
      />

      {/* toolbar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar por descrição..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={onKeyDown}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: !!searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }}
          sx={{ flexGrow: 1, minWidth: 240 }}
        />
        <Button variant="outlined" onClick={onBuscar}>Buscar</Button>
      </Paper>

      {/* tabela */}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="ID" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma marca encontrada
                </TableCell>
              </TableRow>
            ) : sorted.map((m) => (
              <TableRow key={m.id} hover sx={{ cursor: 'pointer' }} onClick={() => abrirForm(m.id)}>
                <TableCell data-label="ID">{m.id}</TableCell>
                <TableCell data-label="Descrição">{m.descricao}</TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => abrirForm(m.id)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton color="error" onClick={() => setConfirmId(m.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {/* paginação */}
      <Stack spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {items.length} de {totalItens} marcas (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar marca' : 'Nova marca'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="sm"
      >
        <FormBrand
          key={form.id || 'nova'}
          embedded
          brandId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      {/* diálogo */}
      <Dialog open={!!confirmId} onClose={() => setConfirmId(null)}>
        <DialogTitle>Excluir Marca</DialogTitle>
        <DialogContent><DialogContentText>Confirmar exclusão?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>Cancelar</Button>
          <Button color="error" onClick={excluir}>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)} anchorOrigin={{vertical:'bottom', horizontal:'right'}}>
        <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
}

export default BrandList;
